// ITEM 4b — **WHAT HAPPENS TO HER COMPOSER WHEN A REPLY IS ANSWERED.**
//
// Pulled out of `src/App.tsx` by R8's ceiling, and pulled out for the reason that ledger keeps
// recording: *the thing that had to move is the thing nothing could reach.* Inside the dashboard
// component this decision could only ever be read by a regex; out here a `.mjs` gate CALLS it.
//
// **IT IMPORTS NOTHING BUT `import type`**, which is the entry requirement for this directory and
// what lets a fixture load eighteen thousand lines of law with no React and no browser.
import type { OwnerAnswer } from "./owner-load";

/** The composer's whole state. `openId: ""` is CLOSED, which is a decision rather than a default. */
export type SupportReplyState = { openId: string; draft: string; sending: boolean; note: string };

export const EMPTY_SUPPORT_REPLY: SupportReplyState = { openId: "", draft: "", sending: false, note: "" };

/**
 * **THE ANSWER'S PAYLOAD DECIDES, NEVER THE STATUS CODE** (R39/R47): this route refuses with HTTP 200
 * and `{ ok: false }`, so a client reading the status would close her composer and tell her a member
 * was answered when the server declined.
 *
 * >>> **THREE OUTCOMES, NEVER TWO (R18), AND THE MIDDLE ONE IS WHY THE DRAFT IS KEPT.** <<< On a
 * refusal her words STAY IN THE BOX and the composer stays open — a desk that clears a reply it
 * failed to send makes her type it twice, and the second time she does not know whether the first
 * one went. On success it closes, because the row is answered and the list is about to re-read.
 */
export function supportReplyNext(
  // The answer's SHAPE is not this law's business — it reads only whether the send was accepted, so a
  // route that grows a field cannot break the composer (R18: what happened, never what came back).
  answer: OwnerAnswer<Record<string, unknown>>,
  { openId, draft, who, refused, via = "" }: { openId: string; draft: string; who: string; refused: string; via?: string },
): SupportReplyState {
  // >>> **T114-1: THE SENTENCE NAMES THE CARRIER, BECAUSE ONE OF THE TWO IS NOW A LIE.** <<< *It is
  // in her inbox* was the only outcome this desk had; a writer with no account is answered by EMAIL,
  // and telling the owner it is in an inbox sends her looking for a thread that does not exist. The
  // FACT comes off the door's answer (`via`) rather than being re-derived here from the row, so the
  // sentence cannot disagree with what really happened (R28/R29) — and an answer with NO `via` says
  // the neutral true thing rather than guessing, which is what every reply sent before this round
  // would report (R18/R19: the unknown case is never worse than today's behaviour).
  if (answer.ok) {
    const where = via === "email" ? " It went to her by email."
      : via === "inbox" ? " It is in her inbox."
      : "";
    return { ...EMPTY_SUPPORT_REPLY, note: `Answered ${who}.${where}` };
  }
  return { openId, draft, sending: false, note: refused };
}

/** Who the note names. Her name if the house has one, else the address she wrote in from. */
export const supportReplyWho = (m: { name?: string; email?: string }) =>
  String(m?.name || "").trim() || String(m?.email || "").trim() || "her";
